/**
 * Entity type detection utilities.
 * Provides type-safe detection of ODA entity types.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { isPhotoMarkerEntity } from "../photomarker/detection";
import { isLeaderEntity as checkIsLeader } from "../leaders/detection";

// ============== Entity Type Detection ==============

/**
 * Entity type identifiers.
 */
export enum EntityType {
  Unknown = "unknown",
  Text = "text",
  MText = "mtext",
  BlockReference = "blockReference",
  Polyline = "polyline",
  Circle = "circle",
  Arc = "arc",
  Ellipse = "ellipse",
  Hatch = "hatch",
  Line = "line",
  MLeader = "mleader",
}

/**
 * Entity types for transform handle configuration.
 * Determines which handles are available for each entity type.
 */
export enum TransformableEntityType {
  Circle = "circle", // Translation + uniform scale only
  Ellipse = "ellipse", // Full transform support
  Arc = "arc", // Translation + uniform scale only
  Text = "text", // Translation only
  MText = "mtext", // Translation + AddLeader handle
  Polyline = "polyline", // Translation only (vertex editing = future)
  Rectangle = "rectangle", // Translation + rotation + resize (special polyline)
  BlockReference = "blockReference", // Translation + rotation + uniform scale
  PhotoMarker = "photoMarker", // Translation + rotation + uniform scale (no non-uniform resize)
  MLeader = "mleader", // AddLeader handle (native OdDbMLeader)
  Hatch = "hatch", // Translation only
  RasterImage = "rasterImage", // Translation + resize (non-uniform scaling)
  Unknown = "unknown", // Translation + rotation only
}

/**
 * Check if an entity is a text entity (OdDbText or OdDbMText).
 * Uses isA() and desc() for type-safe detection (ODA recommended).
 */
export function isTextEntity(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): boolean {
  let entity: DrawingModule.OdDbEntity | null = null;
  try {
    entity = entityId.safeOpenObject(
      OpenMode.kForRead,
      false,
    ) as DrawingModule.OdDbEntity;
    const entityClass = entity.isA();
    return (
      entityClass.isEqualTo(module.OdDbText.desc()) ||
      entityClass.isEqualTo(module.OdDbMText.desc())
    );
  } finally {
    if (entity) deleteObj(entity);
  }
}

/**
 * Check if an entity is an editable text entity (OdDbText, OdDbMText, or OdDbMLeader).
 * MLeaders contain MText content that can be edited through the same UI.
 */
export function isEditableTextEntity(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): boolean {
  return isTextEntity(module, entityId) || checkIsLeader(module, entityId);
}

/**
 * Check if an entity is a block reference (OdDbBlockReference).
 * Uses isA() and desc() for type-safe detection (ODA recommended).
 */
export function isBlockReference(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): boolean {
  let entity: DrawingModule.OdDbEntity | null = null;
  try {
    entity = entityId.safeOpenObject(
      OpenMode.kForRead,
      false,
    ) as DrawingModule.OdDbEntity;
    const entityClass = entity.isA();
    return entityClass.isEqualTo(module.OdDbBlockReference.desc());
  } finally {
    if (entity) deleteObj(entity);
  }
}

/**
 * Check if all entities in an array are text entities.
 */
export function areAllTextEntities(
  module: DrawingModuleType,
  entityIds: DrawingModule.OdDbObjectId[],
): boolean {
  if (entityIds.length === 0) return false;
  return entityIds.every((id) => isTextEntity(module, id));
}

/**
 * Check if all entities in an array are block references.
 */
export function areAllBlockReferences(
  module: DrawingModuleType,
  entityIds: DrawingModule.OdDbObjectId[],
): boolean {
  if (entityIds.length === 0) return false;
  return entityIds.every((id) => isBlockReference(module, id));
}

// Re-export from photomarker module for backwards compatibility
export { isPhotoMarkerEntity } from "../photomarker/detection";

// Re-export from leaders module for convenience
export { isLeaderEntity } from "../leaders/detection";

/**
 * Get the entity type for an entity.
 * Uses isA() and desc() for type-safe detection (ODA recommended).
 */
export function getEntityType(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): EntityType {
  let entity: DrawingModule.OdDbEntity | null = null;
  try {
    entity = entityId.safeOpenObject(
      OpenMode.kForRead,
      false,
    ) as DrawingModule.OdDbEntity;
    const entityClass = entity.isA();

    let result = EntityType.Unknown;

    // Check MText before Text (MText is more specific)
    if (entityClass.isEqualTo(module.OdDbMText.desc()))
      result = EntityType.MText;
    else if (entityClass.isEqualTo(module.OdDbText.desc()))
      result = EntityType.Text;
    else if (checkIsLeader(module, entityId)) result = EntityType.MLeader;
    else if (entityClass.isEqualTo(module.OdDbBlockReference.desc()))
      result = EntityType.BlockReference;
    else if (entityClass.isEqualTo(module.OdDbPolyline.desc()))
      result = EntityType.Polyline;
    // Check Arc before Circle (Arc inherits from Circle in some ODA versions)
    else if (entityClass.isEqualTo(module.OdDbArc.desc()))
      result = EntityType.Arc;
    else if (entityClass.isEqualTo(module.OdDbCircle.desc()))
      result = EntityType.Circle;
    else if (entityClass.isEqualTo(module.OdDbEllipse.desc()))
      result = EntityType.Ellipse;
    else if (entityClass.isEqualTo(module.OdDbHatch.desc()))
      result = EntityType.Hatch;

    return result;
  } finally {
    if (entity) deleteObj(entity);
  }
}

/**
 * Get the transformable entity type for transform handle configuration.
 * This determines which handles are available for the entity.
 * Uses ODA's isA() and desc() methods for type-safe entity detection.
 */
export function getTransformableEntityType(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): TransformableEntityType {
  let entity: DrawingModule.OdDbEntity | null = null;
  let polyline: DrawingModule.OdDbPolyline | null = null;
  try {
    entity = entityId.safeOpenObject(
      OpenMode.kForRead,
      false,
    ) as DrawingModule.OdDbEntity;
    const entityClass = entity.isA();

    let result = TransformableEntityType.Unknown;

    // Check text types first - MText before Text (MText has AddLeader handle)
    if (entityClass.isEqualTo(module.OdDbMText.desc())) {
      result = TransformableEntityType.MText;
    } else if (entityClass.isEqualTo(module.OdDbText.desc())) {
      result = TransformableEntityType.Text;
    }
    // Check block reference - distinguish between photo markers and generic blocks
    else if (entityClass.isEqualTo(module.OdDbBlockReference.desc())) {
      // Check if it's a photo marker (needs special handles)
      if (isPhotoMarkerEntity(module, entityId)) {
        result = TransformableEntityType.PhotoMarker;
      } else {
        result = TransformableEntityType.BlockReference;
      }
    }
    // Check MLeader (native leader annotation entity)
    else if (checkIsLeader(module, entityId)) {
      result = TransformableEntityType.MLeader;
    }
    // Check polyline - need to detect if it's a rectangle
    else if (entityClass.isEqualTo(module.OdDbPolyline.desc())) {
      polyline = module.OdDbPolyline.cast(entity);
      if (polyline) {
        result = isRectanglePolyline(module, polyline)
          ? TransformableEntityType.Rectangle
          : TransformableEntityType.Polyline;
      } else {
        result = TransformableEntityType.Polyline;
      }
    }
    // Check hatch
    else if (entityClass.isEqualTo(module.OdDbHatch.desc())) {
      result = TransformableEntityType.Hatch;
    }
    // Check raster image (translation + resize)
    else if (entityClass.isEqualTo(module.OdDbRasterImage.desc())) {
      result = TransformableEntityType.RasterImage;
    }
    // Check circle (translation + uniform scale only)
    // Note: OdDbArc inherits from OdDbCircle, so check Arc first with isA() for exact match
    else if (entityClass.isEqualTo(module.OdDbArc.desc())) {
      result = TransformableEntityType.Arc;
    } else if (entityClass.isEqualTo(module.OdDbCircle.desc())) {
      result = TransformableEntityType.Circle;
    }
    // Check ellipse (full transform support)
    else if (entityClass.isEqualTo(module.OdDbEllipse.desc())) {
      result = TransformableEntityType.Ellipse;
    }

    return result;
  } finally {
    if (polyline) deleteObj(polyline);
    if (entity) deleteObj(entity);
  }
}

/**
 * Check if a polyline is a rectangle (4 vertices, closed, right angles).
 * Used to determine if full resize handles should be shown.
 */
export function isRectanglePolyline(
  module: DrawingModuleType,
  polyline: DrawingModule.OdDbPolyline,
): boolean {
  // Must have exactly 4 vertices
  const numVerts = polyline.numVerts();
  if (numVerts !== 4) return false;

  // Must be closed
  if (!polyline.isClosed()) return false;

  // Get vertex positions
  const vertices: { x: number; y: number }[] = [];
  for (let i = 0; i < 4; i++) {
    const pt = new module.OdGePoint2d();
    polyline.getPointAt(i, pt);
    vertices.push({ x: pt.x, y: pt.y });
    deleteObj(pt);
  }

  // Check for right angles at each corner
  // A rectangle has perpendicular edges
  for (let i = 0; i < 4; i++) {
    const p0 = vertices[i];
    const p1 = vertices[(i + 1) % 4];
    const p2 = vertices[(i + 2) % 4];

    // Vector from p0 to p1
    const v1x = p1.x - p0.x;
    const v1y = p1.y - p0.y;

    // Vector from p1 to p2
    const v2x = p2.x - p1.x;
    const v2y = p2.y - p1.y;

    // Dot product should be ~0 for perpendicular vectors
    const dot = v1x * v2x + v1y * v2y;
    const len1 = Math.sqrt(v1x * v1x + v1y * v1y);
    const len2 = Math.sqrt(v2x * v2x + v2y * v2y);

    // Normalize and check angle (allow small tolerance)
    if (len1 > 0.0001 && len2 > 0.0001) {
      const cosAngle = Math.abs(dot / (len1 * len2));
      if (cosAngle > 0.01) return false; // Not perpendicular
    }
  }

  return true;
}
